import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { publicAccessSignInRequest } from 'store/modules/auth/actions';
import PublicAccessData from 'services/dataService/PublicAccessData';
import { CpfMaskCustom } from 'components/CpfMaskCustom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f6f9fc',
    padding: theme.spacing(4, 0),
  },
  paper: {
    maxWidth: 520,
    margin: '0 auto',
    padding: theme.spacing(4),
    borderRadius: 8,
  },
  logo: {
    maxHeight: 48,
    maxWidth: 180,
    objectFit: 'contain',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: '#5f6b7a',
    marginBottom: theme.spacing(3),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
}));

export default function PublicAccess() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { companyCourseId } = useParams();
  const loadingAuth = useSelector(state => state.auth.loading);
  const [cpf, setCpf] = useState('');
  const [course, setCourse] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const domain = window.location.hostname;


  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const response = await PublicAccessData.show(companyCourseId);
        if (!mounted) return;
        setCourse(response.data.course);
        setCompany(response.data.company);
      } catch (err) {
        if (!mounted) return;
        setError('Não foi possível validar o acesso.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [companyCourseId]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      dispatch(publicAccessSignInRequest(companyCourseId, cpf, domain));
    },
    [companyCourseId, cpf, domain, dispatch]
  );

  return (
    <div className={classes.root}>
      <Container maxWidth="sm">
        <Paper className={classes.paper} elevation={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box textAlign="center">
              <Typography className={classes.title} variant="h5">
                Acesso indisponível
              </Typography>
              <Typography className={classes.subtitle} variant="body1">
                {error}
              </Typography>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {company?.logo?.url && (
                <img
                  alt={company?.name || 'Logo'}
                  className={classes.logo}
                  src={company.logo.url}
                />
              )}
              <Typography className={classes.title} variant="h5">
                Digite seu CPF para acessar o treinamento
              </Typography>
              <Typography className={classes.subtitle} variant="body1">
                {course?.name}
              </Typography>
              <TextField
                autoFocus
                className={classes.field}
                fullWidth
                label="CPF"
                name="cpf"
                onChange={event => setCpf(event.target.value)}
                InputProps={{
                  inputComponent: CpfMaskCustom,
                }}
                value={cpf}
                variant="outlined"
              />
              <Button
                color="primary"
                disabled={loadingAuth || cpf.replace(/[^\d]/g, '').length !== 11}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {loadingAuth ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </div>
  );
}
